/**
 * US5/T087/T088/T089 (FR-025, FR-026, FR-027, screen-routes.md). The diff an administrator reads
 * before committing an import batch — either kind, since `kind` is never returned to the client
 * (see `List.tsx`'s own note). The batch's number (`IMP-000007`) lives in the route, so a refresh
 * returns to the same batch (unlike `007-inventory`'s stock-upload preview, which has no `GET` route and says so in
 * its own header comment) — `GET /imports/{batchId}/rows` is a real, server-paged, per-outcome
 * read, so this is `PagedTable` again, the same one every server-paged list here uses, structured
 * the way `reports/ReapplyPreview.tsx` structures its own preview → commit/abandon screen.
 *
 * The four counts (T082/T088) come from four `?outcome=&pageSize=1` reads rather than from the
 * upload response, on purpose: `ImportBatchResponse` (the `POST /imports` reply) is not persisted
 * anywhere the client can refetch it from, so a screen that trusted it would go blank on a
 * refresh. Four small paged reads are the honest way to reconstruct the same numbers from
 * `batchId` alone, and they cost one row each, not the batch.
 */
import { useState } from "react";
import { Link, useLoaderData, useOutletContext, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import toast from "react-hot-toast";
import { problemMessage } from "../../components/dashboard/tone";
import { PiArrowLeftBold as ArrowLeft } from "react-icons/pi";
import { Alert, Button, Text } from "rizzui";
import { WidgetCard, EmptyState, ErrorSurface } from "@steelyard/theme";
import { PagedTable } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { can } from "../../lib/permissions";
import { BackLink } from "../../lib/navigation";
import type { AccountSummary, PermissionState } from "../../types";
import {
  abandonImport,
  commitImport,
  getImportRows,
  importBatchByKey,
  importRowOutcomeLabel,
  IMPORT_ROW_OUTCOMES,
  type ImportCommitResult,
  type ImportRowOutcome,
} from "../../lib/services/catalog";
import { importRowColumns } from "./columns";
import { hrefWith, readPaging } from "../../lib/paging";
import { importBatchPath } from "../../lib/paths";

export async function importDiffLoader({ params, request }: LoaderFunctionArgs) {
  const key = params.batchNumber!;
  // The batch's own status comes from its by-number read — a batch someone already committed or
  // abandoned must not offer Commit/Abandon again when reopened. Null only for an old id URL (see
  // `importBatchByKey`): its status is unknown, so the buttons show and the API's 409 still refuses.
  const batch = await importBatchByKey(request, key);
  const batchId = batch?.batchId ?? key;
  const href = importBatchPath(batch?.batchNumber ?? key);
  const url = new URL(request.url);
  const { page, pageSize } = readPaging(url.searchParams);
  const requestedOutcome = url.searchParams.get("outcome");

  // pageSize: 1 — only `totalCount` is read off these four, never `items`.
  const counted = await Promise.all(
    IMPORT_ROW_OUTCOMES.map((outcome) => getImportRows(batchId, { outcome, page: 1, pageSize: 1 })),
  );
  const countByOutcome = Object.fromEntries(
    IMPORT_ROW_OUTCOMES.map((outcome, index) => [outcome, counted[index].totalCount]),
  ) as Record<ImportRowOutcome, number>;

  // Errors first — "the useful question about a 500-row import is almost always show me the
  // errors" (ImportQueryHandler's own comment) — then whichever group actually has rows.
  const priority: ImportRowOutcome[] = ["error", "addition", "change", "unchanged"];
  const outcome: ImportRowOutcome =
    requestedOutcome && (IMPORT_ROW_OUTCOMES as readonly string[]).includes(requestedOutcome)
      ? (requestedOutcome as ImportRowOutcome)
      : (priority.find((candidate) => countByOutcome[candidate] > 0) ?? "error");

  const rows = await getImportRows(batchId, { outcome, page, pageSize });
  const serverStatus = batch?.status ?? "validated";

  return { batchId, href, countByOutcome, outcome, rows, serverStatus };
}

export default function ImportDiffPage() {
  const { batchId, href, countByOutcome, outcome, rows, serverStatus } = useLoaderData() as Awaited<ReturnType<typeof importDiffLoader>>;
  const [searchParams] = useSearchParams();
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();

  const [busy, setBusy] = useState<"commit" | "abandon" | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [commitResult, setCommitResult] = useState<ImportCommitResult | null>(null);
  const [abandoned, setAbandoned] = useState(false);

  if (!can(permissionState, "catalogue.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="review an import" />;
  }

  const query = (over: Record<string, string | undefined>) => hrefWith(href, searchParams, over);

  const canCommit = can(permissionState, "catalogue.administer");
  const resolved = commitResult !== null || abandoned || serverStatus !== "validated";

  async function onCommit() {
    setBusy("commit");
    setError(null);
    try {
      const result = await commitImport(batchId);
      setCommitResult(result);
      toast.success(`${result.rowsApplied} row(s) applied`);
    } catch (err) {
      setError(err);
      // The error surface sits at the top; the button is at the bottom of a long row list.
      toast.error(problemMessage(err, "The commit was refused"));
    } finally {
      setBusy(null);
    }
  }

  async function onAbandon() {
    setBusy("abandon");
    setError(null);
    try {
      await abandonImport(batchId);
      setAbandoned(true);
      toast.success("Import abandoned — nothing was written");
    } catch (err) {
      setError(err);
      toast.error(problemMessage(err, "The batch could not be abandoned"));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex w-full max-w-4xl flex-col gap-6">
      {/* No count on the header: the four outcome counts below are the figures, never a merged total. */}
      <ListHeader
        title="Import Review"
        description="Every row of this batch, grouped by what committing it would do. Nothing is written until the batch is committed."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Data", href: "/imports" }, { name: "Review" }]}
      >
        <BackLink to="/imports">
          <Button as="span" variant="outline">
            <ArrowLeft aria-hidden className="me-1.5 size-4" />
            Back to imports
          </Button>
        </BackLink>
      </ListHeader>

      {error ? <ErrorSurface error={error} action="update this import batch" /> : null}

      {commitResult ? (
        <Alert color="success" bar>
          <p className="font-semibold">Committed.</p>
          <p>
            {commitResult.rowsApplied} row(s) applied, {commitResult.rowsSkipped} skipped (errors and unchanged
            rows are never applied).
          </p>
        </Alert>
      ) : null}

      {abandoned || serverStatus === "abandoned" ? (
        <Alert color="warning" bar>
          This import batch was abandoned. Nothing was written to the catalogue.
        </Alert>
      ) : null}

      {!commitResult && serverStatus === "committed" ? (
        <Alert color="success" bar>
          This import batch has already been committed.
        </Alert>
      ) : null}

      {/* T082/T088: four separate, non-summable counts — never a merged total. */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {IMPORT_ROW_OUTCOMES.map((candidate) => (
          <Link key={candidate} to={query({ outcome: candidate, page: undefined })} aria-current={candidate === outcome ? "true" : undefined}>
            <WidgetCard title={importRowOutcomeLabel(candidate)} className={candidate === outcome ? "ring-primary ring-2" : undefined}>
              <Text className="text-3xl font-bold">{countByOutcome[candidate]}</Text>
            </WidgetCard>
          </Link>
        ))}
      </div>

      <PagedTable
        result={rows}
        caption={`${importRowOutcomeLabel(outcome)} rows, with line number and detail`}
        columns={importRowColumns}
        getRowId={(row) => String(row.lineNumber)}
        // The outcome filter as the toolbar's dropdown; the cards above stay as its linked summary.
        status={{
          label: "Show",
          options: IMPORT_ROW_OUTCOMES.map((candidate) => ({
            label: `${importRowOutcomeLabel(candidate)} only`,
            href: query({ outcome: candidate, page: undefined }),
            active: candidate === outcome,
            count: countByOutcome[candidate],
          })),
        }}
        empty={
          <EmptyState title={`No ${importRowOutcomeLabel(outcome).toLowerCase()} rows`}>
            Nothing in this batch fell into this group.
          </EmptyState>
        }
      />

      {!resolved ? (
        canCommit ? (
          <div className="flex gap-3">
            <Button onClick={onCommit} isLoading={busy === "commit"} disabled={busy !== null}>
              {busy === "commit" ? "Committing…" : "Commit"}
            </Button>
            <Button onClick={onAbandon} isLoading={busy === "abandon"} disabled={busy !== null} variant="outline" color="danger">
              {busy === "abandon" ? "Abandoning…" : "Abandon"}
            </Button>
          </div>
        ) : (
          <Alert color="warning" bar>
            Reviewing only — committing or abandoning this batch needs import-commit access. Ask an administrator
            to act on it, or change the role matrix if you need it.
          </Alert>
        )
      ) : null}
    </div>
  );
}
